import type { AuthoredGridPose, AuthoredGridSpan, TrackBehavior } from '../schema/index.ts';

export const AUTHORED_CATALOG_VERSION = 1 as const;
export const FIXED_UNITS_PER_TILE = 32 as const;
export const METERS_PER_TILE = 4 as const;
export const METERS_PER_FIXED_UNIT = METERS_PER_TILE / FIXED_UNITS_PER_TILE;

export type AuthoredPitch = AuthoredGridPose['pitchDegrees'];
export type AuthoredBank = AuthoredGridPose['bank'];
export type AuthoredOrientation = AuthoredGridPose['orientation'];
export type AuthoredTrackType = 'normal' | 'station' | 'lift' | 'launch' | 'brake' | 'photo';
export type AuthoredPieceCategory =
  'station' | 'straight' | 'slope' | 'turn' | 'bank' | 'operation' | 'inversion';

export interface FixedPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface AuthoredSpanTemplate {
  readonly end: FixedPoint;
  readonly control1: FixedPoint;
  readonly control2: FixedPoint;
  readonly bankStartDegrees?: number;
  readonly bankEndDegrees?: number;
  readonly windingStartDegrees?: number;
  readonly windingEndDegrees?: number;
}

export interface AuthoredPieceDescriptor {
  readonly id: string;
  readonly label: string;
  readonly category: AuthoredPieceCategory;
  readonly family: string;
  readonly description: string;
  readonly entry: {
    readonly pitches: readonly AuthoredPitch[];
    readonly banks: readonly AuthoredBank[];
    readonly orientations: readonly AuthoredOrientation[];
  };
  readonly exit: {
    readonly headingDelta: number;
    readonly pitch: AuthoredPitch | 'preserve';
    readonly bank: AuthoredBank | 'preserve';
    readonly orientation: AuthoredOrientation | 'preserve';
    readonly windingQuarterTurnsDelta: number;
  };
  readonly spans: readonly AuthoredSpanTemplate[];
  readonly allowedTrackTypes: readonly AuthoredTrackType[];
  readonly mirrorId?: string;
  readonly tags: readonly string[];
  readonly supportStyle: 'standard' | 'sparse_inversion' | 'station' | 'none';
}

export type AuthoredDescriptorRegistry = ReadonlyMap<string, AuthoredPieceDescriptor>;

export interface AuthoredPieceCandidate {
  readonly descriptor: AuthoredPieceDescriptor;
  readonly entryPose: AuthoredGridPose;
  readonly exitPose: AuthoredGridPose;
  readonly spans: readonly AuthoredGridSpan[];
  readonly behavior: TrackBehavior;
}

export interface AuthoredPieceAvailability {
  readonly descriptorId: string;
  readonly allowed: boolean;
  readonly reason: string | null;
}

export interface AuthoredStationGridDelta {
  readonly forwardTiles: number;
  readonly rightTiles: number;
  readonly elevationLevels: number;
  readonly requiredHeadingStep: number;
  readonly signedHeadingChangeSteps: number;
}

export type AuthoredBuildResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly code: string; readonly message: string };
